import { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, orderBy, query, limit } from 'firebase/firestore';
import { useAtomValue } from 'jotai';
import { db } from 'utils/firebase';
import { selectedCompanyIdAtom } from 'atoms/company';
import { lastViewedActivityFeedAtom, getUnviewedActivityFeedCount } from 'atoms';
import { useGetMeQuery } from 'redux-store/api/auth';
import { IPost } from '_interfaces/post.interface';

const POSTS_LIMIT = 20;

export const useProjectActivityFeed = (projectId: number) => {
  const [posts, setPosts] = useState<IPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const companyId = useAtomValue(selectedCompanyIdAtom);
  const lastViewedMap = useAtomValue(lastViewedActivityFeedAtom);
  const { data: userMe } = useGetMeQuery();

  useEffect(() => {
    if (!projectId || !companyId) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const collectionPath = `companies/${companyId}/projects/${projectId}/activityFeed`;
    const postQuery = query(
      collection(db, collectionPath),
      orderBy('createdAt', 'desc'),
      limit(POSTS_LIMIT)
    );

    const unsubscribe = onSnapshot(postQuery, (snapshot) => {
      const postData: IPost[] = snapshot.docs.map((doc) =>
        IPost.fromDocumentData({
          ...doc.data(),
          id: doc.id,
          parentPath: collectionPath,
          companyId: String(companyId),
          projectId: String(projectId),
        })
      );

      const sorted = postData.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      setPosts(sorted);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [projectId, companyId]);

  const recentPosts = useMemo(
    () => posts.filter((post) => post.createdBy?.userId !== String(userMe?.id)).slice(0, 10),
    [posts, userMe?.id]
  );

  const unviewedCount = useMemo(
    () => getUnviewedActivityFeedCount(posts, projectId, userMe?.id || '', lastViewedMap),
    [posts, projectId, userMe?.id, lastViewedMap]
  );

  return { posts, recentPosts, unviewedCount, isLoading };
};
